import { ImagePlagiarismCheckerResponseModel } from './../../models/image-plagiarism-checker';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import {  firstValueFrom, Subscription } from 'rxjs';
import { ProxyServiceFetchURLService } from '../../../../../../../shared/services/proxy-service-fetch-url.service';
import { PackService } from '../../../../../../contributor/components/profile/contributors/pack/services/pack.service';
import { ImageFormat } from '../../../gallery/icon-gallery/models/image-format';
import { IconGalleryService } from '../../../gallery/icon-gallery/services/iconGallery.service';
import { NgForm } from '@angular/forms';
import { AiService } from '../../services/ai.service';
import { ImageUpScalarRequestModel, ImageUpScalarResponseModel } from '../../models/image-enhancer';
import { ImagePlagiarismCheckerRequestModel } from '../../models/image-plagiarism-checker';
import { BlobService } from '../../../../../../../shared/services/blob.service';
import { syncBlobTokens } from '../../../../../../../shared/utils/blob-token-sync';
type Tabs = 'Original' | 'View-Report';

@Component({
  selector: 'app-image-plagiarism-checker',
    standalone:false,

  templateUrl: './image-plagiarism-checker.component.html',
  styleUrl: './image-plagiarism-checker.component.css'
})
export class ImagePlagiarismCheckerComponent implements OnInit {
  activeTab: Tabs = 'Original';
  fileError: string | null = null;
  isFileSelected!: boolean;
  allowedExtensions: string[] = ['jpg', 'png', 'jpeg'];
  imagePlagiarismCheckerRequestModel: ImagePlagiarismCheckerRequestModel = new ImagePlagiarismCheckerRequestModel();
  imagePlagiarismCheckerResponseModel: ImagePlagiarismCheckerResponseModel | null = null;
  isLoading: boolean = false;
  token!: string;
  imageUrl: string | ArrayBuffer | null = null;
  isDownloading: boolean = false;
  @ViewChild('imagePlagiarismCheckerForm') imagePlagiarismCheckerForm!: NgForm;
  imagePlagiarismCheckerSubscription!: Subscription;
  @ViewChild('pdfContent', { static: false }) pdfContent!: ElementRef;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private aiService: AiService,
    public proxyServiceFetchURLService: ProxyServiceFetchURLService,
    private packService: PackService,
    private iconGalleryService: IconGalleryService,
    private cdr: ChangeDetectorRef,
    private blobService:BlobService,
  ) {
    syncBlobTokens({
      blobService: this.blobService,
      destroyRef: this.destroyRef,
      setToken: (t) => (this.token = t ?? ''),
      cdr: this.cdr,
    });
  }

  async ngOnInit() {

    this.preventBrowserDefaults();
  }

  onSelect(event: Event): void {
    this.fileError = null;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const fileSizeMB = file.size / (1024*1024);
      if(fileSizeMB > 10){
        this.clear();
        this.fileError = 'File size exceeds 10MB. Please select a smaller file.';
        return;
      }
      if (fileExtension && this.allowedExtensions.includes(fileExtension)) {
        this.handleFileSelection(file);
      } else {
        this.imagePlagiarismCheckerRequestModel.file = null;
        this.imagePlagiarismCheckerForm.resetForm(this.imagePlagiarismCheckerRequestModel);
        this.isFileSelected = true;
        this.fileError = `Invalid file type.Please select a valid file (${this.allowedExtensions.join(
          ','
        )}).`;
      }
    }
  }
  handleFileSelection(file: File): void {
    if (file) {
      this.imagePlagiarismCheckerRequestModel.file = file;
      this.isFileSelected = true;
      const reader = new FileReader();
      reader.onload = (e) => (this.imageUrl = reader.result);
      reader.readAsDataURL(file);
      this.imagePlagiarismChecker();
    } else {
      this.imagePlagiarismCheckerRequestModel.file = null;
      this.isFileSelected = true;
    }
  }

  get isValidFile(): boolean {
    if (!this.imagePlagiarismCheckerRequestModel.file) {
      return false;
    }
    const fileName = this.imagePlagiarismCheckerRequestModel.file.name.toLowerCase();
    return (
      fileName.endsWith(ImageFormat.Png.toString().toLowerCase()) ||
      fileName.endsWith(ImageFormat.Jpeg.toString().toLowerCase()) ||
      fileName.endsWith(ImageFormat.Jpeg.toString().toLowerCase())
    );
  }

  clearFile(fileInput: HTMLInputElement): void {
    fileInput.value = '';
    this.imagePlagiarismCheckerRequestModel.file = null;
    this.isFileSelected = false;
    this.fileError = null;
    this.isLoading = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.fileError = null;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fakeInputEvent = {
        target: { files: [file] },
      } as unknown as Event;
      this.onSelect(fakeInputEvent);
    }
  }

  allowDrop(event: DragEvent) {
    event.preventDefault();
  }

  preventBrowserDefaults() {
    ['dragover', 'drop'].forEach((eventName) => {
      window.addEventListener(
        eventName,
        (e) => {
          e.preventDefault();
        },
        false
      );
    });
  }

  clear(): void {
    this.imagePlagiarismCheckerRequestModel.file = null;
    this.isFileSelected = false;
    this.fileError = null;
    this.imageUrl = null;
    this.isLoading = false;
    this.isDownloading = false;
    this.activeTab = 'Original';
    this.imagePlagiarismCheckerResponseModel=null;
    this.imagePlagiarismCheckerForm.resetForm();
    if (this.imagePlagiarismCheckerSubscription) {
      this.imagePlagiarismCheckerSubscription.unsubscribe();
    }
    this.cdr.detectChanges();
  }

  async imagePlagiarismChecker() {
    this.isLoading = true;
    this.fileError = null;
    this.activeTab = 'View-Report';
    this.imagePlagiarismCheckerSubscription = await this.aiService
      .imagePlagiarismChecker(this.imagePlagiarismCheckerRequestModel)
      .subscribe({
        next: (data: ImagePlagiarismCheckerResponseModel) => {
          this.imagePlagiarismCheckerResponseModel = data;
          this.isLoading = false;
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 500);
        },
        error: (err) => {
          this.isLoading = false;
          this.fileError = err?.error?.errors?.[0].message || err?.error?.errors?.[0].errors?.[0];
        },
      });
  }



  setTab(tab: Tabs): void {
    this.activeTab = tab;
  }

  activeClass(tab: Tabs) {
    return tab === this.activeTab ? ' active' : '';
  }
  getBadgeClass(value: number): string {
    if (value >= 80) return 'badge badge-danger';
    if (value >= 50) return 'badge badge-warning';
    return 'badge badge-primary';
  }


  ngOnDestroy(): void {
    if (this.imagePlagiarismCheckerSubscription) {
      this.imagePlagiarismCheckerSubscription.unsubscribe();
    }
  }
}
